import { LoadingProgress } from '@issuemanager/apps/client/components';
import {
  useAllQueries,
  useMonitorManageIssueFormLoad,
} from '@issuemanager/apps/client/hooks';
import { useIssueTemplateByIssueTypeAndIssueSubtype } from '@issuemanager/client/features/issue-templates';
import {
  useMoveToDraft,
  useSaveIssue,
  useSubmitToInReview,
} from '@issuemanager/client/features/issues';
import { useSLAsByIssueTypeAndIssueSubtype } from '@issuemanager/client/features/slas';
import { useFeatureFlags } from '@issuemanager/client/features/users';
import { IssueStatusEnum, IssueTypeEnum } from '@issuemanager/constants';
import { Stack, useEventCallback } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  IssueApprovePanel,
  IssueDeclinePanel,
} from '../components/panels/issue';
import { useHeaderButtons } from '../hooks/use-header-buttons';
import { useIssueStatusInfo } from '../hooks/use-issue-status-info';
import {
  ActionField,
  useManageIssueForm,
} from '../hooks/use-manage-issue-form';
import useResetFormData from '../hooks/use-reset-form-data';
import { useIssueOutletContext } from '../hooks/useIssueOutletContext';
import { ActionFields } from './action-fields';
import { Documents } from './documents';
import { IssueFields, RemediationSection } from './issue-fields';

export default function ManageIssue(): React.JSX.Element {
  const { t } = useTranslation(['common', 'issue', 'action']);
  const { code, issue, userRoles: issueUserRoles } = useIssueOutletContext();
  const monitorManageIssueLoad = useMonitorManageIssueFormLoad();
  const isIssueTypesExtraFieldsEnabled =
    useFeatureFlags('issueTypesExtraFields') ?? false;
  const { enqueueSnackbar } = useSnackbar();

  const { isEditable, isInitiated } = useIssueStatusInfo({
    status: issue.data?.status,
    isInitiated: issue.data?.isInitiated,
    approvalStatus: issue.data?.approvalStatus,
  });

  const [selectedPanel, setSelectedPanel] = useState<
    'decline' | 'approve' | null
  >(null);

  const [isEditing, setIsEditing] = useState(false);

  const SLAs = useSLAsByIssueTypeAndIssueSubtype({
    issueType: issue.data?.issueType?.name ?? '',
    issueSubtype: issue.data?.issueSubtype?.id ?? 'null',
  });

  const issueTemplate = useIssueTemplateByIssueTypeAndIssueSubtype({
    issueTypeId: issue.data?.issueType?.id ?? '',
    issueSubtypeId: issue.data?.issueSubtype?.id,
    enabled:
      issue.data?.status === IssueStatusEnum.OPEN &&
      issue.data &&
      !issue.data.selectedTemplate,
  });

  const isIssueManager = Boolean(issueUserRoles.data?.isManager);
  const isIssueCreator = Boolean(issueUserRoles.data?.isCreator);
  const isIssueTypeManager = Boolean(issueUserRoles.data?.isIssueTypeManager);
  const isIssueOwner = Boolean(issueUserRoles.data?.isOwner);

  const canManageWorkflows =
    isIssueTypeManager || isIssueManager || isIssueOwner;

  const isDraftIssue = issue.data?.status === IssueStatusEnum.DRAFT;
  const isIssueOpen = issue.data?.status === IssueStatusEnum.OPEN;

  const messageKey = useMemo(() => {
    const isInProgress = issue.data?.status === IssueStatusEnum.IN_PROGRESS;

    if (isInProgress) {
      if (isEditing) {
        return 'issue:manage.saveAndInitiate.success';
      }
      return 'issue:manage.initiate.success';
    }

    if (isEditing) {
      return 'issue:actions.saveAndApproveFromInReviewSuccess';
    }

    return 'issue:actions.approveFromInReviewSuccess';
  }, [isEditing, issue.data?.status]);

  const handleSubmitSuccess = (): void => {
    enqueueSnackbar(t(messageKey), {
      variant: 'success',
    });

    if (isEditing) {
      setIsEditing(false);
    }
  };

  const isReadOnly = useMemo(() => {
    if (isDraftIssue) {
      if (isIssueTypesExtraFieldsEnabled) {
        return (
          !isEditing ||
          (isEditing &&
            !isIssueCreator &&
            !isIssueTypeManager &&
            !isIssueManager)
        );
      }
      return !isEditing || !isIssueCreator;
    }

    return (
      !isEditing ||
      (isEditing &&
        Boolean(isInitiated) &&
        !isIssueManager &&
        !isIssueTypeManager)
    );
  }, [
    isDraftIssue,
    isIssueTypesExtraFieldsEnabled,
    isEditing,
    isIssueCreator,
    isIssueTypeManager,
    isInitiated,
    isIssueManager,
  ]);

  const {
    manageIssueForm,
    assignIssueIsLoading,
    initiateWorkflowIsLoading,
    manageIssueHandleSave,
    manageIssueHandleSubmit,
    issueFields,
    actionFields,
    onAddAction,
    watched: { actions, executionType },
  } = useManageIssueForm({
    SLAs: SLAs.data,
    issueTemplate: issueTemplate.data,
    readOnly: isReadOnly,
    onSubmitSuccess: handleSubmitSuccess,
  });

  const handleSaveSuccess = (): void => {
    enqueueSnackbar(t('issue:manage.save.success'), {
      variant: 'success',
    });
    setIsEditing(false);
  };

  const saveIssue = useSaveIssue({ code, onSuccess: handleSaveSuccess });

  const handleSubmitToInReviewSuccess = (): void => {
    enqueueSnackbar(t('issue:actions.submitSuccess'), {
      variant: 'success',
    });
  };

  const submitToInReviewMutation = useSubmitToInReview({
    code,
    onSuccess: handleSubmitToInReviewSuccess,
  });

  const handleMoveToDraftSuccess = (): void => {
    enqueueSnackbar(t('issue:actions.declineFromInReviewSuccess'), {
      variant: 'success',
    });
  };

  const moveToDraftMutation = useMoveToDraft({
    code,
    onSuccess: handleMoveToDraftSuccess,
  });

  const handleSubmitToInReviewClick = useEventCallback(() => {
    manageIssueHandleSubmit(async () => {
      submitToInReviewMutation.mutate();
    });
  });

  const handleMoveToDraftClick = useEventCallback(() => {
    moveToDraftMutation.mutate();
  });

  const handleSave = useEventCallback(() => {
    const issueStatus = issue.data?.status;
    if (!issueStatus) return;

    manageIssueHandleSave(saveIssue);
  });

  useAllQueries({
    queries: [issue],
    onSuccess: () => {
      monitorManageIssueLoad.stop();
    },
    onError: () => {
      monitorManageIssueLoad.error();
    },
  });

  const isLoading =
    issue.isFetching ||
    SLAs.isFetching ||
    issueTemplate.isFetching ||
    saveIssue.isPending;

  useHeaderButtons({
    manageIssueForm,
    assignIssueIsLoading,
    initiateWorkflowIsLoading,
    manageIssueHandleSubmit,
    isEditing,
    setIsEditing,
    isLoading,
    activateDeclinePanel: () => setSelectedPanel('decline'),
    activateApprovePanel: () => setSelectedPanel('approve'),
    handleSave,
    saveIssue,
    handleSubmitToInReview: isDraftIssue
      ? handleSubmitToInReviewClick
      : undefined,
    handleMoveToDraft:
      isIssueOpen && isIssueTypeManager ? handleMoveToDraftClick : undefined,
    submitToInReviewIsPending: submitToInReviewMutation.isPending,
    moveToDraftIsPending: moveToDraftMutation.isPending,
  });

  const { resetForm } = useResetFormData({
    manageIssueForm,
  });

  useEffect(() => {
    resetForm();
  }, [resetForm, issue.dataUpdatedAt]);

  const handleDragEnd = useEventCallback((draggedActions: ActionField[]) => {
    manageIssueForm.setValue('actions', draggedActions);
    manageIssueForm.trigger('actions');
  });

  const actionValues = useMemo(() => {
    if (issue.data?.actions.length) {
      return issue.data?.actions;
    }
    return [];
  }, [issue.data?.actions]);

  const renderContent = (): React.JSX.Element => {
    if (isLoading) {
      return <LoadingProgress height={600} />;
    }

    return (
      <FormProvider {...manageIssueForm}>
        <IssueFields
          title={issueFields.title}
          description={issueFields.description}
          severity={issueFields.severity}
          dueDate={issueFields.dueDate}
          govmapGroups={issueFields.govmapGroups}
          impact={issueFields.impact}
          ownerId={issueFields.ownerId}
          approverId={issueFields.approverId}
          managerId={issueFields.managerId}
          issueType={issue.data?.issueType?.name}
          {...(isIssueTypesExtraFieldsEnabled && {
            reviewStatus: issueFields.reviewStatus,
            datePublished: issueFields.datePublished,
            classification: issueFields.classification,
            resolution: issueFields.resolution,
            rootCause: issueFields.rootCause,
            recommendations: issueFields.recommendations,
            managementResponse: issueFields.managementResponse,
            remediation: issueFields.remediation,
          })}
        />

        <Documents
          control={manageIssueForm.control}
          name='attachments'
          isEditing={isEditing}
          attachments={issue.data?.attachments ?? []}
          isIssueEditable={isEditable}
          onEdit={() => setIsEditing(true)}
        />

        {isIssueTypesExtraFieldsEnabled &&
          issue.data?.issueType?.name === IssueTypeEnum.AUDIT_FINDING &&
          issueFields.includeInAuditReport && (
            <Stack spacing={3}>{issueFields.includeInAuditReport}</Stack>
          )}

        {isIssueTypesExtraFieldsEnabled && (
          <RemediationSection
            issueType={issue.data?.issueType?.name}
            reviewStatus={issueFields.reviewStatus}
            resolution={issueFields.resolution}
            managementResponse={issueFields.managementResponse}
            remediation={issueFields.remediation}
          />
        )}

        {!isDraftIssue && (
          <>
            <ActionFields
              issueFields={issueFields}
              actionFields={actionFields}
              actionsFormValues={actions}
              actionValues={actionValues}
              onAddAction={onAddAction}
              isEditing={isEditing}
              onEdit={canManageWorkflows ? () => setIsEditing(true) : undefined}
              executionType={executionType}
              isEditable={isEditable}
              isWorkflowBlocked={issue.data?.isWorkflowBlocked}
              onDragEnd={handleDragEnd}
            />
            <IssueDeclinePanel
              actions={issue.data?.actions || []}
              code={code}
              isOpen={selectedPanel === 'decline'}
              onClose={() => setSelectedPanel(null)}
            />
            <IssueApprovePanel
              code={code}
              isOpen={selectedPanel === 'approve'}
              onClose={() => setSelectedPanel(null)}
            />
          </>
        )}
      </FormProvider>
    );
  };

  return (
    <Stack data-testid='manage-issue' spacing={3} p={4}>
      {renderContent()}
    </Stack>
  );
}
